import { FunctionComponent, ReactElement } from 'react';
import { Location, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { BaseButton } from '../../components/DesignSystem';
import { Domain, GeoLoc, TRACKER_TYPE } from '../../constants/constants';
import { useTracker } from '../../hooks/useWebAnalyse';
import { FormText, Title1 } from '../../styles/TypoStyle';
import { getQueryParams } from '../../utils/QueryParamsUtils';
import logoCTLM from './assets/cetelem-logo.svg';
import cetelemLogoMobile from './assets/cLogo.svg';
import logoCM from './assets/creditModerne-logo.svg';
import creditoError from './assets/credito-error.svg';

const ErrorPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: visible;
`;

const ErrorHeader = styled.div`
  display: flex;
  flex-direction: column;
  //Desktop + Desktop HD
  @media (min-width: 1024px) {
    img {
      height: 33px;
      width: 119px;
    }
  }
  //tablet + mobile
  @media (max-width: 1024px) {
    img {
      width: 25px;
      height: 36px;
    }
  }
  //tablet
  @media (min-width: 496px) and (max-width: 1024px) {
    margin-top: 22.3px;
    margin-left: 17.52px;
  }
  //mobile
  @media (max-width: 496px) {
    margin-top: 12.3px;
    margin-left: 17.52px;
  }
  //Desktop HD
  @media (min-width: 1280px) {
    margin-top: 39.62px;
    margin-left: 176.72px;
  }

  //Desktop
  @media (min-width: 1024px) and (max-width: 1280px) {
    margin-top: 39.62px;
    margin-left: 64.72px;
  }
`;

const ErrorDetails = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 18px;
  //tablet
  @media (min-width: 496px) and (max-width: 1024px) {
    margin: 56px 146px 0px 152px;
  }
`;

const ErrorBody = styled.div`
  display: flex;
  //tablet + mobile
  @media (max-width: 1024px) {
    flex-direction: column-reverse;
  }
  //Desktop + Desktop HD
  @media (min-width: 1024px) {
    flex-direction: row;
    align-self: center;
  }

  //mobile
  @media (max-width: 496px) {
    margin: 16px 16px 0px 16px;
  }
`;

const ErrorText = styled.div`
  display: flex;
  flex-direction: column;
  span {
    margin-top: 24px;
  }

  //tablet + desktop
  @media (min-width: 496px) {
    span {
      white-space: pre-line;
    }
  }

  //Desktop + Desktop HD
  @media (min-width: 1024px) {
    margin-top: 151.73px;
  }
`;

const ErrorButtons = styled.div`
  display: inline-flex;
  flex-direction: row;
  margin-top: 40px;
  gap: 16px;
  base-button {
    --light-white: var(--colors-on-primary);
    --green-50: var(--colors-primary-variant-light);
    --green-500: var(--colors-primary-default);
    --green-800: var(--colors-primary-variant-dark);

    font-family: var(--typo-form-font-family);
    font-size: var(--typo-form-font-size);
    letter-spacing: var(--typo-form-letter-spacing);
    line-height: var(--typo-form-line-height);
    font-weight: bold;
    align-self: stretch;

    //Desktop + tablet
    @media (min-width: 496px) {
      --padding-button: 11px 15px;
    }
  }

  .secondary-button {
    --medium-grey: var(--colors-primary-default);
    --grey-border: var(--colors-primary-default);
  }

  //mobile
  @media (max-width: 496px) {
    flex-direction: column-reverse;
    margin-bottom: 40px;
  }
`;

const CreditoContainer = styled.div`
  img {
    width: 424px;
    height: 344px;
  }
  //Desktop + Desktop HD
  @media (min-width: 1024px) {
    margin-top: 139.73px;
    img {
      width: 424px;
      height: 344px;
    }
  }
  //tablet
  @media (min-width: 496px) and (max-width: 1024px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 104px;
  }

  //tablet + mobile
  @media (max-width: 1024px) {
    img {
      width: 288px;
      height: 232px;
    }
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const Bold = styled(FormText)`
  font-weight: var(--typo-title4-font-weight);
`;

const ErrorTitle = styled(Title1)`
  //mobile
  @media (max-width: 496px) {
    font-size: var(--typo-title2-font-size);
    line-height: var(--typo-title2-line-height);
  }
  //tablet + desktop
  @media (min-width: 1024px) {
    white-space: pre-line;
  }
  //mobile
  @media (max-width: 496px) {
    width: 212px;
  }
`;

const ErrorPage: FunctionComponent<Record<string, never>> = (): ReactElement => {
  const location: Location = useLocation();
  const { geoLocation } = getQueryParams();
  const host = window.location.hostname;
  //const [geoLocVal, dispatchGeoLocVal] = useState<GeoLoc>(GeoLoc.NONE);
  const geoLocVal =
    host.includes(Domain.CTLM) || host.includes(Domain.LOCAL)
      ? GeoLoc.CTLM
      : host.includes(Domain.CM) && (geoLocation === 'cmoi' || geoLocation === 'CMOI')
      ? GeoLoc.CMOI
      : host.includes(Domain.CM) && (geoLocation === 'cmag' || geoLocation === 'CMAG')
      ? GeoLoc.CMAG
      : GeoLoc.NONE;

  //web analyse hook
  useTracker(
    [TRACKER_TYPE.P4],
    undefined,
    undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (location.state as unknown as any)?.code,
    undefined,
    geoLocVal,
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  console.error('error code ' + (location.state as unknown as any)?.code);

  const redirectToConseiller = () => {
    window.location.replace((window as unknown as WindowWithEnv)._env_.conseillerUrl);
  };

  const redirectToAccueil = () => {
    window.location.replace((window as unknown as WindowWithEnv)._env_.cetelemUrl);
  };

  const logo = host.includes(Domain.CTLM) || host.includes(Domain.LOCAL) ? logoCTLM : logoCM;

  return (
    <ErrorPageContainer>
      <ErrorHeader>
        <picture>
          <source srcSet={`${logo}`} media="(min-width: 1024px)" />
          <img srcSet={`${cetelemLogoMobile}`} />
        </picture>
      </ErrorHeader>
      <ErrorBody>
        <ErrorDetails>
          <ErrorText>
            <ErrorTitle>{`Désolé, une erreur s’est \n produite`}</ErrorTitle>
            <FormText>{`Nous rencontrons un problème qui empêche la finalisation \nde  votre demande.`}</FormText>
            <FormText>
              {`Vous pouvez`} <Bold>{`contacter un de nos conseillers`}</Bold>{' '}
              {`pour vous \naccompagner dans votre démarche de souscription.`}
            </FormText>
          </ErrorText>
          <ErrorButtons>
            <BaseButton onClick={redirectToConseiller} secondary className="secondary-button">
              Contacter un conseiller
            </BaseButton>
            <BaseButton onClick={redirectToAccueil}>Retour à la page d’accueil</BaseButton>
          </ErrorButtons>
        </ErrorDetails>
        <CreditoContainer>
          <img src={creditoError} />
        </CreditoContainer>
      </ErrorBody>
    </ErrorPageContainer>
  );
};

export default ErrorPage;
